// src/enemy.js

const ENEMY_TYPES = {
    brawler: {
        hpMultiplier: 1,
        speedMultiplier: 1,
        cooldownMultiplier: 1,
        rangeMultiplier: 1,
        behavior: "direct",
        label: "brute",
        tint: [255, 255, 255],
    },
    sprinter: {
        hpMultiplier: 0.75,
        speedMultiplier: 1.45,
        cooldownMultiplier: 0.8,
        rangeMultiplier: 0.9,
        behavior: "zigzag",
        label: "sprinteur",
        tint: [140, 210, 255],
    },
    tank: {
        hpMultiplier: 1.7,
        speedMultiplier: 0.75,
        cooldownMultiplier: 1.15,
        rangeMultiplier: 1.15,
        behavior: "bully",
        label: "tank",
        tint: [255, 170, 140],
    },
};

export function spawnEnemy(k, state, options = {}) {
    if (state.gameOver || state.menuActive) return;

    const {
        BASE_ENEMY_HP,
        ENEMY_SPEED,
        ENEMY_ATTACK_COOLDOWN,
        ENEMY_PUNCH_RANGE,
        ARENA_PADDING,
        HUD_HEIGHT,
        ENEMY_SPEED_WAVE_SCALING,
        ENEMY_ATTACK_COOLDOWN_WAVE_SCALING,
        MIN_ENEMY_ATTACK_COOLDOWN,
    } = state.constants;
    
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

    const typeKey = options.type && ENEMY_TYPES[options.type] ? options.type : "brawler";
    const type = ENEMY_TYPES[typeKey];
    const foeHp = Math.max(1, Math.round((BASE_ENEMY_HP + (state.wave - 1)) * type.hpMultiplier));
    const foeSpeed = ENEMY_SPEED * Math.pow(ENEMY_SPEED_WAVE_SCALING, state.wave - 1) * type.speedMultiplier;
    const foeAttackCooldown = Math.max(
        MIN_ENEMY_ATTACK_COOLDOWN,
        ENEMY_ATTACK_COOLDOWN * Math.pow(ENEMY_ATTACK_COOLDOWN_WAVE_SCALING, state.wave - 1) * type.cooldownMultiplier,
    );
    const foePunchRange = ENEMY_PUNCH_RANGE * type.rangeMultiplier;

    const foe = k.add([
        k.sprite("foe-avatar", { anim: "idle" }),
        k.pos(k.width() * 0.7, k.height() * 0.6),
        k.anchor("center"),
        k.area(),
        k.color(...type.tint),
        k.scale(1.6),
        k.z(5),
        {
            hp: foeHp,
            maxHp: foeHp,
            attackTimer: foeAttackCooldown * 0.5,
            isDefeated: false,
            type: typeKey,
            typeLabel: type.label,
            behavior: type.behavior,
            speed: foeSpeed,
            punchRange: foePunchRange,
            dashTimer: 0.5 + Math.random() * 1.1,
            dashTime: 0,
            moveSeed: Math.random() * 10,
        },
        "foe",
    ]);

    const foeHalfW = (foe.width ?? 32) / 2;
    const foeHalfH = (foe.height ?? 32) / 2;
    const foeMinX = ARENA_PADDING + foeHalfW;
    const foeMaxX = k.width() - ARENA_PADDING - foeHalfW;
    const foeMinY = HUD_HEIGHT + 30 + foeHalfH;
    const foeMaxY = k.height() - ARENA_PADDING - foeHalfH;
    const defaultSpawnX = clamp(k.width() * 0.72, foeMinX, foeMaxX);
    const defaultSpawnY = clamp(k.height() * 0.6, foeMinY, foeMaxY);
    const spawnPos = options.spawnPos ? options.spawnPos : k.vec2(defaultSpawnX, defaultSpawnY);
    foe.pos = k.vec2(clamp(spawnPos.x, foeMinX, foeMaxX), clamp(spawnPos.y, foeMinY, foeMaxY));
    foe.play("idle");

    const foeShadow = k.add([
        k.rect(52, 18, { radius: 9 }),
        k.pos(foe.pos.x, foe.pos.y + 20),
        k.anchor("center"),
        k.color(0, 0, 0),
        k.opacity(0.22),
        k.z(4),
    ]);

    foe.onUpdate(() => {
        if (!state.player || !state.player.exists() || foe.isDefeated || state.menuActive) return;

        const dir = state.player.pos.sub(foe.pos);
        const distance = dir.len();
        const baseDir = distance > 0 ? dir.unit() : k.vec2(1, 0);
        let moveDir = baseDir;
        let speedBoost = 1;

        if (foe.behavior === "zigzag") {
            const perp = k.vec2(-baseDir.y, baseDir.x);
            const sway = Math.sin(k.time() * 6 + foe.moveSeed) * 0.55;
            moveDir = baseDir.add(perp.scale(sway)).unit();
            foe.dashTimer -= k.dt();
            if (foe.dashTimer <= 0 && distance > foe.punchRange * 1.4) {
                foe.dashTimer = 1.1 + Math.random() * 1.1;
                foe.dashTime = 0.25;
            }
            if (foe.dashTime > 0) {
                foe.dashTime -= k.dt();
                speedBoost = 1.65;
            }
        } else if (foe.behavior === "bully") {
            if (distance < foe.punchRange * 1.1) {
                speedBoost = 0.2;
            }
        }

        if (distance > 1) {
            const movement = moveDir.scale(foe.speed * speedBoost * k.dt());
            foe.pos = foe.pos.add(movement);
        }

        foe.pos.x = clamp(foe.pos.x, foeMinX, foeMaxX);
        foe.pos.y = clamp(foe.pos.y, foeMinY, foeMaxY);
        foeShadow.pos = k.vec2(foe.pos.x, foe.pos.y + 20);

        foe.attackTimer -= k.dt();

        if (foe.attackTimer <= 0 && distance <= foe.punchRange) {
            foe.attackTimer = foeAttackCooldown;
            enemyPunch(k, foe, state);
        }
    });

    foe.onDestroy(() => {
        state.currentEnemy = null;
        if (state.enemies) {
            state.enemies = state.enemies.filter((enemy) => enemy !== foe);
        }
        if (foeShadow.exists()) {
            k.destroy(foeShadow);
        }
    });

    return foe;
}

function enemyPunch(k, foe, state) {
    if (!foe.exists()) return;

    const dir = state.player.pos.sub(foe.pos);
    const base = dir.len() > 0 ? dir.unit() : k.vec2(1, 0);
    if (!foe.isDefeated) {
        foe.play("punch");
        k.wait(0.28, () => {
            if (foe.exists() && !foe.isDefeated) {
                foe.play("idle");
            }
        });
    }
    k.play("sfx_punch", { volume: 0.45 });
    const hit = k.add([
        k.rect(14, 10),
        k.area(),
        k.pos(foe.pos.add(base.scale(18))),
        k.opacity(1),
        k.lifespan(0.14, { fade: 0.05 }),
        k.color(255, 190, 160),
        "foePunch",
    ]);

    hit.onCollide("player", () => {
        state.damagePlayer(1);
        state.spawnImpactBurst(state.player.pos.add(base.scale(-4)), k.rgb(255, 120, 90));
    });
}
